import React, { useState, useEffect, useCallback, SyntheticEvent } from 'react';
import { Modal, Button, Select, Input, Tooltip, Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import { createFlow } from '../../../lib/api/flow-api';
import { getModelsForAlgorithm } from '../../../lib/api/algorithm-api';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';
import { UploadFilesStep } from '../upload/UploadFilesStep';
import { Batch, Model } from '../../../types';

const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => any;
  algorithmId: number;
};
const CreateClassificationFlow = (props: Props) => {
  const { visible, onClose, algorithmId } = props;

  const history = useHistory();

  const [batchUploadReports, setBatchUploadReports] = useState<Batch[]>([]);
  const [name, setName] = useState<null | string>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [model, setModel] = useState<null | Model>(null);
  const [step, setStep] = useState<number>(0);
  const [isCreating, setCreating] = useState(false);

  const classes = (model && model.info && model.info.annotationTypes) || [];

  const fetchModels = useCallback(async () => {
    const models = await getModelsForAlgorithm(algorithmId);
    setModels(models);
  }, [algorithmId]);

  useEffect(() => {
    if (algorithmId) {
      fetchModels();
    }
  }, [algorithmId, fetchModels]);

  const onNameChange = (value: SyntheticEvent<HTMLInputElement>) => {
    setName(value.currentTarget.value);
  };

  const onModelChange = (value: number) => {
    const selectedModel = models.find(model => model.id === value);
    setModel(selectedModel || null);
  };

  const goToUpload = async () => {
    setStep(1);
  };

  const onSubmit = async () => {
    if (!name || !model) return;

    setCreating(true);

    const flow = await createFlow(name, model.id, { fields: [] }, batchUploadReports);

    history.push(`/flows/${algorithmId}/${flow.id}`);
    onModalClose();

    setCreating(false);
  };

  const onModalClose = async () => {
    setModel(null);
    setName(null);
    setStep(0);

    onClose();
  };

  const nextButtonIsDisabled = () => {
    if (step === 0) {
      return !name || name.length === 0 || !model;
    } else if (step === 1) {
      return isCreating || !batchUploadReports || batchUploadReports.length === 0;
    }
    return false;
  };

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={<ModalTitle>New flow</ModalTitle>}
      onCancel={() => onModalClose()}
      footer={
        step === 0 ? (
          <div>
            <Button type="default" onClick={() => onModalClose()}>
              Cancel
            </Button>
            <Tooltip title={nextButtonIsDisabled() ? 'Enter a name and select a model before continuing.' : ''}>
              <Button
                style={{ marginLeft: '10px' }}
                type="primary"
                onClick={goToUpload}
                disabled={nextButtonIsDisabled()}>
                Next
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <Button type="default" onClick={() => onModalClose()}>
              Cancel
            </Button>
            <Tooltip title={nextButtonIsDisabled() ? 'Upload at least 1 document.' : ''}>
              <Button
                type="primary"
                onClick={onSubmit}
                style={{ marginLeft: '10px' }}
                disabled={nextButtonIsDisabled()}>
                Analyze
              </Button>
            </Tooltip>
          </div>
        )
      }>
      {step === 0 ? (
        <div>
          <FormItemLabel>This process will create a new model.</FormItemLabel>
          <Input
            placeholder="Name"
            value={name || undefined}
            onChange={onNameChange}
            style={{ margin: '0px 0px 10px 0px' }}
          />
          <ModalSubTitle>Classes</ModalSubTitle>
          <FormItemLabel>Select an existing model.</FormItemLabel>
          {models && (
            <Select style={{ width: 200 }} onChange={onModelChange}>
              {models.map(model => (
                <Option key={model.id} value={model.id}>
                  {model.name}
                </Option>
              ))}
            </Select>
          )}
          <div>
            {classes.map((className, index) => {
              return (
                <Tag
                  key={index}
                  style={{
                    padding: '5px',
                    marginTop: '10px',
                    backgroundColor: '#ffffff',
                    borderColor: '#e8e8e8',
                  }}>
                  <span
                    style={{
                      color: '#6d6e77',
                      margin: '5px',
                      fontSize: '12px',
                    }}>
                    {className}
                  </span>
                </Tag>
              );
            })}
          </div>
        </div>
      ) : (
        <UploadFilesStep batchUploadReports={batchUploadReports} setBatchUploadReports={setBatchUploadReports} />
      )}
    </Modal>
  );
};

export default CreateClassificationFlow;
